import React, { useState, useEffect } from 'react';
import { Edit, FormGroupContextProvider, TabbedForm, useCreatePath, useNotify, useRedirect } from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { Container, Typography } from '@mui/material';
import { Theme } from 'src/Theme';
import { ShsReturnButton, AutoSaveFormListener, ShsSuccessNotification } from 'src/components/common';
import { employeeEditContainerStyles } from 'src/styles/components/employee/employee';
import { useFormAutoSave } from 'src/hooks/useFormAutoSave';
import EmploymentTypeForm from './form/EmploymentTypeForm';
import EmployeeDocumentsForm from './form/EmployeeDocumentsForm';
import EmployeePersonalDetailsForm from './form/EmployeePersonalDetailsForm';
import tabGroups from './const/tab-groups';
import EmployeeEditToolbar from './form/EmployeeEditToolbar';
import { CancelEditEmployeeModal } from './modals/CancelEditEmployeeModal';
import {
    CheckIsEmployeeActive,
    isEmployeeInactive,
    updateDataForReActivation
} from 'src/utils/employees/employee-helper';
import { AUTOSAVE_RESOURCES } from 'src/constants/autoSave';
import { NOTIFICATION_OPTIONS_SUCCESS, getDeactivationSuccessMessage } from 'src/constants';
import { useNotification, NOTIFICATIONS_REFRESHED_EVENT } from 'src/utils/notification';
import { EmployeeType } from './types';

/**
 * Employee Edit page with three tabs
 */
const EmployeeEdit: React.FC = () => {
    const form = useForm();

    return (
        <FormProvider {...form}>
            <InnerEmployeeEdit />
        </FormProvider>
    );
};

const InnerEmployeeEdit: React.FC = () => {
    const redirect = useRedirect();
    const createPath = useCreatePath();
    const notify = useNotify();
    const notification = useNotification();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { storageKey, clearAutoSave, markAsSubmitting, record } = useFormAutoSave(AUTOSAVE_RESOURCES.EMPLOYEE);
    const isInactiveEmployee = isEmployeeInactive(record as CheckIsEmployeeActive);

    // If isInactiveEmployee - need to erase the data from 2nd & 3d tabs
    useEffect(() => {
        if (isInactiveEmployee && record && !record.__isResetForReActivation) {
            updateDataForReActivation(record);
            record.__isResetForReActivation = true;
        }
    }, [isInactiveEmployee, record]);

    /**
     * Executed on successful edit and displays a success notification
     */
    const onSuccess = (options?: { isDeactivation?: boolean }) => {
        markAsSubmitting();
        clearAutoSave();
        const notificationMessage = options?.isDeactivation
            ? getDeactivationSuccessMessage(record.name, record.surname)
            : 'Редагування працівника пройшло успішно';
        notify(<ShsSuccessNotification message={notificationMessage} />, NOTIFICATION_OPTIONS_SUCCESS);
        notification.refetchNotifications().then((data) => {
            window.dispatchEvent(new CustomEvent(NOTIFICATIONS_REFRESHED_EVENT, { detail: data }));
        });
    };

    /**
     * Opens the cancel edit modal and clears auto-save
     */
    const handleOpenModal = () => {
        clearAutoSave();
        setIsModalOpen(true);
    };

    /**
     * Closes the cancel edit modal
     */
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    /**
     * Redirects to the employee's profile page after successful edit
     */
    const handleRedirect = () => {
        redirect(createPath({ resource: `employees/${record.id}/show`, type: 'show' }));
    };

    return (
        <Container sx={employeeEditContainerStyles} maxWidth={false}>
            <ShsReturnButton label="Назад до профілю працівника" onClick={handleOpenModal} />
            <Edit<EmployeeType> actions={false} redirect="show" mutationOptions={{ onSuccess: () => onSuccess() }}>
                <Typography variant="h4" component="h3" mb={4} fontWeight="700">
                    {isInactiveEmployee ? 'Активувати' : 'Редагування'} працівника
                </Typography>
                <TabbedForm
                    toolbar={
                        <EmployeeEditToolbar
                            deleteLabel="Видалити працівника"
                            submitLabel="Зберегти"
                            enableCancelButton={isInactiveEmployee}
                            editMode
                            handleCancelModalFromParent={handleOpenModal}
                            onCancel={handleRedirect}
                            onSuccess={onSuccess}
                        />
                    }>
                    <TabbedForm.Tab label="Персональна інформація">
                        {storageKey && <AutoSaveFormListener storageKey={storageKey} />}
                        <FormGroupContextProvider name={tabGroups[0]}>
                            <EmployeePersonalDetailsForm />
                        </FormGroupContextProvider>
                    </TabbedForm.Tab>
                    <TabbedForm.Tab label="Документи">
                        <FormGroupContextProvider name={tabGroups[1]}>
                            <EmployeeDocumentsForm />
                        </FormGroupContextProvider>
                    </TabbedForm.Tab>
                    <TabbedForm.Tab label="Працевлаштування">
                        <FormGroupContextProvider name={tabGroups[2]}>
                            <EmploymentTypeForm isEditForm={true} />
                        </FormGroupContextProvider>
                    </TabbedForm.Tab>
                    {isModalOpen && (
                        <CancelEditEmployeeModal
                            isOpen={isModalOpen}
                            isEdit={true}
                            isInactiveEmployee={isInactiveEmployee}
                            handleRedirect={handleRedirect}
                            onClose={handleCloseModal}
                            onSuccess={onSuccess}
                        />
                    )}
                </TabbedForm>
            </Edit>
        </Container>
    );
};

export default EmployeeEdit;
